<template>
  <div class="result-screen bg-white rounded-3xl shadow-2xl p-6 md:p-10 max-w-4xl mx-auto">
    <!-- Header -->
    <div class="result-header text-center mb-8">
      <div class="result-emoji text-6xl md:text-8xl mb-4 animate-bounce">{{ scoreEmoji }}</div>
      <h2 class="result-title text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
        {{ performanceMessage }}
      </h2>
      <p class="result-subtitle text-lg md:text-xl text-gray-600 font-medium">{{ quizTitle }}</p>
    </div>

    <!-- Score Summary -->
    <div class="score-summary grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <div class="summary-card bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
        <div class="summary-label text-xs md:text-sm text-purple-700 font-medium mb-1">Your Score</div>
        <div class="summary-value score text-2xl md:text-3xl font-bold text-purple-900">
          {{ result.total_score }}
        </div>
      </div>

      <div class="summary-card bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
        <div class="summary-label text-xs md:text-sm text-blue-700 font-medium mb-1">Accuracy</div>
        <div class="summary-value text-2xl md:text-3xl font-bold text-blue-900">{{ result.accuracy }}%</div>
      </div>

      <div class="summary-card bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
        <div class="summary-label text-xs md:text-sm text-green-700 font-medium mb-1">Time</div>
        <div class="summary-value text-2xl md:text-3xl font-bold text-green-900">{{ formattedTime }}</div>
      </div>

      <div class="summary-card bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border-2 border-yellow-200">
        <div class="summary-label text-xs md:text-sm text-yellow-700 font-medium mb-1">Correct</div>
        <div class="summary-value text-2xl md:text-3xl font-bold text-yellow-900">
          {{ result.correct_answers }}/{{ result.total_questions }}
        </div>
      </div>
    </div>

    <!-- Question Breakdown -->
    <div v-if="userAnswers && questions" class="questions-breakdown mb-8">
      <h3 class="section-title text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <i class="fas fa-list-check text-purple-600"></i>
        Question Breakdown
      </h3>
      <div class="questions-list space-y-3">
        <div
          v-for="(answer, index) in userAnswers"
          :key="index"
          class="question-item bg-white rounded-lg p-4 border-2 transition-all hover:shadow-md"
          :class="answer.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'"
        >
          <div class="flex items-start gap-3">
            <div class="question-number flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                 :class="answer.isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white'">
              {{ index + 1 }}
            </div>
            <div class="flex-1">
              <div class="question-text text-sm md:text-base text-gray-800 mb-2">
                {{ getQuestionText(answer.question_id) }}
              </div>
              <div class="question-stats flex flex-wrap gap-3 text-xs md:text-sm">
                <span class="stat-badge px-3 py-1 rounded-full font-medium"
                      :class="answer.isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'">
                  <i :class="answer.isCorrect ? 'fas fa-check' : 'fas fa-times'"></i>
                  {{ answer.isCorrect ? 'Correct' : 'Wrong' }}
                </span>
                <span class="stat-badge bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                  <i class="fas fa-clock"></i>
                  {{ answer.time_spent }}s
                </span>
                <span class="stat-badge bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-medium">
                  <i class="fas fa-star"></i>
                  {{ getQuestionPoints(answer) }} pts
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Leaderboard Position (if available) -->
    <div v-if="result.leaderboard_update" class="leaderboard-section mb-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-200">
      <h3 class="section-title text-xl md:text-2xl font-bold text-amber-900 mb-4 flex items-center gap-2">
        <i class="fas fa-trophy text-amber-500"></i>
        Your Ranking
      </h3>
      <div class="ranking-cards flex gap-4 justify-center flex-wrap">
        <div class="rank-card global bg-white rounded-lg p-4 border-2 border-amber-300 shadow-md min-w-[140px] text-center">
          <div class="rank-label text-sm text-gray-600 mb-1">Global Rank</div>
          <div class="rank-value text-3xl font-bold text-amber-600">
            #{{ result.leaderboard_update.global_rank }}
          </div>
        </div>
        <div
          v-if="result.leaderboard_update.country_rank"
          class="rank-card country bg-white rounded-lg p-4 border-2 border-amber-300 shadow-md min-w-[140px] text-center"
        >
          <div class="rank-label text-sm text-gray-600 mb-1">
            {{ result.leaderboard_update.quiz_country_code }} Rank
          </div>
          <div class="rank-value text-3xl font-bold text-amber-600">
            #{{ result.leaderboard_update.country_rank }}
          </div>
        </div>
      </div>
    </div>

    <!-- Perfect Score Confetti -->
    <div v-if="result.is_perfect_score" class="confetti-container absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
      <div class="confetti"></div>
      <div class="confetti"></div>
      <div class="confetti"></div>
      <div class="confetti"></div>
      <div class="confetti"></div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons flex flex-col md:flex-row gap-3 md:gap-4">
      <button
        v-if="canRetry"
        @click="$emit('try-again')"
        class="action-btn primary bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-6 rounded-xl font-bold text-base md:text-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
      >
        <i class="fas fa-redo"></i>
        Try Again ({{ result.attempts_remaining }} left)
      </button>

      <button
        v-if="relatedSlug"
        @click="$emit('return-to-source')"
        class="action-btn secondary bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 px-6 rounded-xl font-bold text-base md:text-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
      >
        <i class="fas fa-arrow-left"></i>
        Return to Article/Place
      </button>

      <button
        @click="$emit('view-leaderboards')"
        class="action-btn secondary bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 px-6 rounded-xl font-bold text-base md:text-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
      >
        <i class="fas fa-trophy"></i>
        View Leaderboards
      </button>

      <button
        @click="$emit('return-to-map')"
        class="action-btn tertiary bg-gradient-to-r from-gray-500 to-gray-600 text-white py-3 px-6 rounded-xl font-bold text-base md:text-lg hover:from-gray-600 hover:to-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
      >
        <i class="fas fa-map"></i>
        Return to Map
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResultScreen',
  props: {
    result: {
      type: Object,
      required: true
      // Full quiz result object from backend
    },
    quizTitle: {
      type: String,
      required: true
    },
    relatedSlug: {
      type: String,
      default: null
      // Slug for return-to-article/place navigation
    },
    userAnswers: {
      type: Array,
      default: () => []
      // Array of user answers with question_id, selected_option_id, time_spent, isCorrect
    },
    questions: {
      type: Array,
      default: () => []
      // Array of quiz questions with id, text, options, correct_option_id
    }
  },
  emits: ['try-again', 'return-to-map', 'view-leaderboards', 'return-to-source'],
  methods: {
    /**
     * Get question text by question ID
     */
    getQuestionText(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      return question ? question.text : 'Question not found'
    },

    /**
     * Calculate points for a question based on correctness and time
     */
    getQuestionPoints(answer) {
      if (!answer.isCorrect) return 0

      // Base points for correct answer
      let points = 100

      // Time bonus: faster answers get more points (max 50 bonus points)
      // Assuming 30s is the baseline, < 10s gets full bonus
      if (answer.time_spent < 10) {
        points += 50
      } else if (answer.time_spent < 20) {
        points += 25
      } else if (answer.time_spent < 30) {
        points += 10
      }

      return points
    }
  },
  computed: {
    /**
     * Get emoji based on score performance
     * @returns {string} Emoji
     */
    scoreEmoji() {
      if (this.result.is_perfect_score) return '🏆'
      if (this.result.accuracy >= 80) return '🌟'
      if (this.result.accuracy >= 60) return '👍'
      return '📚'
    },

    /**
     * Get performance message based on accuracy
     * @returns {string} Performance message
     */
    performanceMessage() {
      if (this.result.is_perfect_score) return 'Perfect Score! Incredible!'
      if (this.result.accuracy >= 80) return 'Excellent Work!'
      if (this.result.accuracy >= 60) return 'Good Job!'
      return 'Nice Try! Keep Learning!'
    },

    /**
     * Format time spent (MM:SS)
     * @returns {string} Formatted time
     */
    formattedTime() {
      const minutes = Math.floor(this.result.time_spent / 60)
      const seconds = this.result.time_spent % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },

    /**
     * Check if user can retry the quiz
     * @returns {boolean} Can retry
     */
    canRetry() {
      return this.result.attempts_remaining > 0
    }
  }
}
</script>

<style scoped>
/* Confetti animation for perfect scores */
.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #f39c12;
  animation: confetti-fall 3s linear infinite;
}

.confetti:nth-child(1) {
  left: 10%;
  animation-delay: 0s;
  background: #e74c3c;
}

.confetti:nth-child(2) {
  left: 30%;
  animation-delay: 0.5s;
  background: #3498db;
}

.confetti:nth-child(3) {
  left: 50%;
  animation-delay: 1s;
  background: #2ecc71;
}

.confetti:nth-child(4) {
  left: 70%;
  animation-delay: 1.5s;
  background: #f39c12;
}

.confetti:nth-child(5) {
  left: 90%;
  animation-delay: 2s;
  background: #9b59b6;
}

@keyframes confetti-fall {
  0% {
    top: -10%;
    transform: rotate(0deg);
  }
  100% {
    top: 110%;
    transform: rotate(720deg);
  }
}

/* Button hover animations */
.action-btn {
  transition: all 0.3s ease;
}

.action-btn:active {
  transform: translateY(0) scale(0.98);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .result-emoji {
    font-size: 3rem;
  }

  .result-title {
    font-size: 1.75rem;
  }

  .summary-value {
    font-size: 1.5rem;
  }
}
</style>
